import { useMemo, useEffect } from 'react';
import { useAtom } from 'ximple';
import { useIntentMulti } from '@repo/widget-utils/services/hooks/membership';
import { getCartItemId } from '@repo/widget-utils/cart/cartUtils';
import { useAtomSelector } from 'src/hooks/useSelector';
import useIntentMultiRequest from 'src/hooks/domain/cart/useIntentMultiRequest';
import useCartItemsWithMembershipAddedOrRemoved from 'src/hooks/domain/cart/useCartItemsWithMembershipAddedOrRemoved';
import {
    setIsLoadingPrices,
    setCartItems,
    BookingState,
    BookingActions,
    PackageOrSingleQuantities,
} from './bookingReducer';
import { cartAtom } from '../cart/cartAtom';
import { voucherAtom } from '../voucher/voucherAtom';
import { checkoutInfoAtom, selectIsCompanyContact } from '../checkout-info/checkoutInfoAtom';
import { useMemberContext } from 'src/widgets/timeslots/timeslots/MemberContext';

type Props<T extends PackageOrSingleQuantities> = Pick<BookingState<T>, 'newCartItem'> & {
    bookingDispatch: React.Dispatch<BookingActions<T>>;
};

export function useBookingEffects<T extends PackageOrSingleQuantities>({
    newCartItem,
    bookingDispatch,
}: Props<T>) {
    const { loggedInUser, currentSite } = useMemberContext();
    const [cartItemsInCart] = useAtom(cartAtom);
    const [{ appliedPromoCode, ignoredValueCards }] = useAtom(voucherAtom);
    const isCompanyContact = useAtomSelector(checkoutInfoAtom, selectIsCompanyContact);

    const cartItemsToFetchPaymentPlansFor = useMemo(
        () =>
            newCartItem
                ? [
                      ...cartItemsInCart.filter(
                          (x) => getCartItemId(x) !== getCartItemId(newCartItem),
                      ),
                      newCartItem,
                  ]
                : [],
        [newCartItem, cartItemsInCart],
    );

    const {
        request: currentPaymentPlanRequest,
        isUsingPaymentPlan,
        isRequestChanging,
    } = useIntentMultiRequest(
        isCompanyContact,
        cartItemsToFetchPaymentPlansFor,
        appliedPromoCode,
        ignoredValueCards,
    );

    const { data: paymentPlanResponse, isLoading: isLoadingPaymentPlan } = useIntentMulti(
        currentPaymentPlanRequest,
        loggedInUser,
        currentSite?.key,
    );

    const cartItemsMembership = useCartItemsWithMembershipAddedOrRemoved(
        cartItemsToFetchPaymentPlansFor,
        isUsingPaymentPlan,
        paymentPlanResponse ?? null,
    );

    // Set value to indicate if prices are loading or not.
    useEffect(() => {
        bookingDispatch(
            setIsLoadingPrices(isUsingPaymentPlan && (isLoadingPaymentPlan || isRequestChanging)),
        );
    }, [isUsingPaymentPlan, isLoadingPaymentPlan, isRequestChanging, bookingDispatch]);

    // Set cart-items with payment-plan
    useEffect(() => {
        bookingDispatch(setCartItems(cartItemsMembership));
    }, [cartItemsMembership, bookingDispatch]);
}
